import { useEffect, useRef, useState } from 'react'
import { supabase } from '../supabaseClient'
import { runSync } from './syncEngine'

const MIN_BACKOFF_MS = 3000
const MAX_BACKOFF_MS = 30000

export function useNetworkStatus() {
    const [isOnline, setIsOnline] = useState(navigator.onLine)
    const backoffRef = useRef(MIN_BACKOFF_MS)
    const wasOnlineRef = useRef(navigator.onLine)
    const fallbackTimerRef = useRef(null)

    useEffect(() => {
        const clearFallback = () => {
            if (fallbackTimerRef.current) {
                clearTimeout(fallbackTimerRef.current)
                fallbackTimerRef.current = null
            }
            backoffRef.current = MIN_BACKOFF_MS
        }

        const setStatus = (reachable) => {
            const cameBackOnline = !wasOnlineRef.current && reachable
            wasOnlineRef.current = reachable
            setIsOnline(reachable)

            if (cameBackOnline) {
                if (typeof runSync === 'function') {
                    runSync()
                } else {
                    console.error('[useNetworkStatus] runSync is not a function — try a hard refresh (Ctrl+Shift+R) to clear a stale bundle.')
                }
            }
            if (reachable) clearFallback()
        }

        // Adaptive fallback polling — only ever active while we believe we're offline.
        const scheduleFallbackCheck = () => {
            fallbackTimerRef.current = setTimeout(async () => {
                let reachable = false
                try {
                    const { error } = await supabase.from('branches').select('branch_id').limit(1)
                    reachable = !error
                } catch {
                    reachable = false
                }
                if (reachable) {
                    setStatus(true)
                } else {
                    backoffRef.current = Math.min(backoffRef.current * 2, MAX_BACKOFF_MS)
                    scheduleFallbackCheck()
                }
            }, backoffRef.current)
        }

        // Primary signal: realtime socket connectivity, not a REST poll.
        const channel = supabase.channel('network-status-probe')
        channel.subscribe((status) => {
            if (status === 'SUBSCRIBED') {
                setStatus(true)
            } else if (['CHANNEL_ERROR', 'TIMED_OUT', 'CLOSED'].includes(status)) {
                setStatus(false)
                if (!fallbackTimerRef.current) scheduleFallbackCheck()
            }
        })

        const onBrowserOnline = () => setStatus(true)
        const onBrowserOffline = () => {
            setStatus(false)
            if (!fallbackTimerRef.current) scheduleFallbackCheck()
        }
        window.addEventListener('online', onBrowserOnline)
        window.addEventListener('offline', onBrowserOffline)

        return () => {
            clearFallback()
            window.removeEventListener('online', onBrowserOnline)
            window.removeEventListener('offline', onBrowserOffline)
            supabase.removeChannel(channel)
        }
    }, [])

    return isOnline
}